import { useMemo } from 'react';
import { parseQuickAdd, type QuickAddResult, type RecurrenceOptions } from '@karya/core';
import { useTimeSettings, useWorkspace } from '@/data/queries';
import * as m from '@/data/mutations';

/** Live natural-language parsing for quick add (dates, !priority, #tags, /list, *repeat). */
export function useQuickParse(
  text: string,
  ignored: Array<[number, number]>,
  currentSpaceId: string | null,
  now: Date,
): QuickAddResult {
  const ws = useWorkspace();
  const { tz } = useTimeSettings();
  return useMemo(
    () =>
      parseQuickAdd(text, {
        now,
        tz,
        lists: ws.lists.map((l) => ({
          id: l.id,
          name: l.name,
          emoji: l.emoji,
          spaceId: l.space_id,
          spaceName: ws.spaceById.get(l.space_id)?.name ?? null,
        })),
        tags: ws.tags.map((t) => ({ id: t.id, name: t.name, space_id: t.space_id })),
        currentSpaceId,
        ignoredRanges: ignored,
      }),
    [text, ignored, currentSpaceId, now, tz, ws],
  );
}

/** Create a task from a parsed quick-add result, creating any new tags inline. */
export async function createFromParse(
  parsed: QuickAddResult,
  base: {
    listId: string;
    spaceId: string;
    dueDate?: string | null;
    statusId?: string | null;
    parentTaskId?: string | null;
    priority?: number | null;
    extraTagIds?: string[];
    description?: string | null;
  },
) {
  const listId = parsed.list?.id ?? base.listId;
  const spaceId = parsed.list?.spaceId ?? base.spaceId;
  const tagIds = [...(base.extraTagIds ?? [])];
  for (const t of parsed.tags) {
    if (t.id) tagIds.push(t.id);
    else tagIds.push((await m.createTag(spaceId, t.name)).id);
  }
  const due = parsed.due_date ?? base.dueDate ?? null;
  return m.createTask({
    name: parsed.name || 'Untitled task',
    list_id: listId,
    parent_task_id: base.parentTaskId ?? null,
    status_id: listId === base.listId ? (base.statusId ?? null) : null,
    due_date: due,
    due_time: due ? parsed.due_time : null,
    priority: (parsed.priority ?? base.priority ?? null) as 1 | 2 | 3 | 4 | null,
    tag_ids: [...new Set(tagIds)],
    description_md: base.description ?? null,
    recurrence: parsed.recurrence ? quickRecurrence(parsed.recurrence.kind) : null,
    recurrence_rrule: parsed.recurrence?.rrule ?? null,
  });
}

/** Default options for a repeat typed in quick add (kept here so the recurrence engine loads lazily). */
function quickRecurrence(kind: RecurrenceOptions['preset']): RecurrenceOptions {
  return {
    mode: 'on_complete',
    basis: 'schedule',
    create_new_task: true,
    reset_status_id: null,
    end: { type: 'never' },
    skip_weekends: false,
    copy_subtasks: true,
    copy_checklists: 'reset',
    preset: kind,
  };
}
